/**
 * Calcula la racha actual de cardio del cliente (días consecutivos con
 * al menos 1 sesión registrada) y si ya hubo registro HOY.
 *
 * Se extrajo de CardioHistorySection para poder mostrar la racha también
 * ARRIBA del aro de cardio (CardioGoalCard), igualando el patrón de la
 * sección de Nutrición donde el `CoachStreakCard` vive arriba del aro.
 *
 * El hook hace su propio fetch acotado a los últimos 40 días (suficiente
 * para calcular una racha realista). Si en algún momento queremos
 * unificar el fetch con el del heatmap (que pide 12 semanas), valdría
 * la pena un contexto compartido — por ahora son ~30 filas, el costo
 * es despreciable.
 */

import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CardioLog {
  log_date: string;
}

interface Result {
  streak: number;
  loggedToday: boolean;
  loading: boolean;
}

const LOOKBACK_DAYS = 40;

function todayLocal(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatLocalDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function useCardioStreak(
  userId: string | null | undefined,
  profileName: string | null | undefined,
  refreshKey?: number,
): Result {
  const [logs, setLogs] = useState<CardioLog[] | null>(null);

  useEffect(() => {
    if (!userId) {
      setLogs([]);
      return;
    }
    let cancelled = false;
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - (LOOKBACK_DAYS - 1));
    const startStr = formatLocalDate(start);
    let query = supabase
      .from('exercise_logs')
      .select('log_date')
      .eq('user_id', userId)
      .eq('exercise_type', 'cardio')
      .gte('log_date', startStr);
    if (profileName) query = query.eq('profile_name', profileName);
    query.then(({ data }) => {
      if (cancelled) return;
      setLogs((data || []) as CardioLog[]);
    });
    return () => { cancelled = true; };
  }, [userId, profileName, refreshKey]);

  const streak = useMemo(() => {
    if (!logs || logs.length === 0) return 0;
    const distinctDays = new Set(logs.map(l => l.log_date));
    let count = 0;
    const cursor = new Date(todayLocal() + 'T12:00:00');
    let isFirstDay = true;
    let safety = 0;
    while (safety++ < LOOKBACK_DAYS) {
      const key = formatLocalDate(cursor);
      if (distinctDays.has(key)) {
        count++;
      } else if (!isFirstDay) {
        break;
      }
      isFirstDay = false;
      cursor.setDate(cursor.getDate() - 1);
    }
    return count;
  }, [logs]);

  const loggedToday = useMemo(() => {
    if (!logs) return false;
    const t = todayLocal();
    return logs.some(l => l.log_date === t);
  }, [logs]);

  return { streak, loggedToday, loading: logs === null };
}
